import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import PartAutocomplete from '../shared/PartAutocomplete';
import { formatPriceWithCurrency } from '../../utils/vat';
import { partsSaleErrorMessage } from './salesUtils';

/**
 * Manage the POS quick-pick grid: add parts from the registry or saved jobs
 * (Työlajit), remove, reorder. The parent owns the picks list and refetches on
 * change (onChanged).
 */
function QuickPickManageDialog({ open, onClose, picks, onChanged }) {
  const { t } = useTranslation();
  const [error, setError] = useState('');

  const addPick = async (body) => {
    setError('');
    try {
      await axios.post('/api/parts-sales-quick-picks/', body);
      onChanged();
    } catch (e) {
      setError(partsSaleErrorMessage(e, t, 'parts_sales.quickPickAddFailed'));
    }
  };

  const remove = async (pick) => {
    setError('');
    try {
      await axios.delete(`/api/parts-sales-quick-picks/${pick.id}/`);
      onChanged();
    } catch (e) {
      setError(t('parts_sales.quickPickRemoveFailed'));
    }
  };

  const move = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= picks.length) return;
    const ids = picks.map((p) => p.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setError('');
    try {
      await axios.post('/api/parts-sales-quick-picks/reorder/', { ids });
      onChanged();
    } catch (e) {
      setError(t('parts_sales.quickPickReorderFailed'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle>{t('parts_sales.manageQuickPicks')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('parts_sales.quickPicksHelp')}
        </Typography>
        <PartAutocomplete
          onSelect={(part) => addPick({ part_id: part.id })}
          onSelectJob={(job) => addPick({ job_id: job.id })}
          jobsToggle
          label={t('parts_sales.addQuickPick')}
          placeholder={t('parts_sales.scanOrPickPlaceholder')}
          inputTestId="quick-pick-add-input"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <List dense sx={{ mt: 1 }}>
          {picks.map((pick, i) => (
            <ListItem
              key={pick.id}
              divider
              // Three action buttons — reserve room so long part names never run
              // under them (MUI's default padding fits only one action).
              sx={{ pr: 14 }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t('parts_sales.moveUp')}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={i === picks.length - 1} onClick={() => move(i, 1)} aria-label={t('parts_sales.moveDown')}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => remove(pick)} aria-label={t('parts_sales.removeQuickPick')} data-testid={`quick-pick-remove-${pick.id}`}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={
                  pick.kind === 'job' ? (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      <Chip label={t('parts_sales.jobTag')} size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '10px' }} />
                      {pick.job_name}
                    </Box>
                  ) : (
                    pick.part_name
                  )
                }
                secondary={[
                  pick.kind === 'job' ? pick.job_code : pick.part_code,
                  `${formatPriceWithCurrency(parseFloat(pick.unit_price))}${pick.unit === 'h' ? `/${t('parts_sales.hourUnit')}` : ''}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItem>
          ))}
          {picks.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {t('parts_sales.noQuickPicks')}
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="secondary" data-testid="quick-pick-manage-close">
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QuickPickManageDialog;
